import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import type { MethodologyResponse } from '@/api/schema/data-contracts';
import type { OmitSafely } from '@/types';

import type { EstimationItemsGroupedTableProps } from '../../EstimationItemsGroupedTables';
import EstimationItemsGroupedTables from '../../EstimationItemsGroupedTables';
import { mapFromResourceEstimationItemsTableRecord } from '../../mappers/estimationItemsEditFormModelMapper';
import type { EstimationItemsEditFormModel, ResourceEstimationItemsTableRecord } from '../../model';

interface TopLevelEstimationItemsFormProps {
  onSubmit: (formModel: EstimationItemsEditFormModel) => Promise<void>;
  topLevelEstimationItems: ResourceEstimationItemsTableRecord[];
  methodologies: MethodologyResponse[];
  estimationMethodologyId: string;
  tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    | 'estimationItems'
    | 'control'
    | 'onIncludeClicked'
    | 'onMethodologyChange'
    | 'onResourceFieldBlur'
    | 'formModelEstimationItems'
  >;
}

export const TopLevelEstimationItemsForm: FC<TopLevelEstimationItemsFormProps> = ({
  onSubmit,
  topLevelEstimationItems,
  estimationMethodologyId,
  tableProps,
}) => {
  const formModelInitial = useMemo<EstimationItemsEditFormModel>(
    () => ({
      estimationItems: topLevelEstimationItems.map((x) =>
        mapFromResourceEstimationItemsTableRecord(
          x,
          estimationMethodologyId,
          tableProps.estimationModel,
        ),
      ),
    }),
    [topLevelEstimationItems, tableProps.estimationModel, estimationMethodologyId],
  );

  const { control, handleSubmit, reset, watch } = useForm<EstimationItemsEditFormModel>({
    mode: 'onChange',
    defaultValues: formModelInitial,
  });
  const formModelEstimationItems = watch('estimationItems');

  useEffect(() => {
    reset(formModelInitial, { keepDirtyValues: true });
  }, [formModelInitial, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EstimationItemsGroupedTables
        control={control}
        formModelEstimationItems={formModelEstimationItems}
        estimationItems={topLevelEstimationItems}
        onResourceFieldBlur={handleSubmit(onSubmit)}
        showEstimateColumn={true}
        {...tableProps}
      />
      {/* The submit button is here, but just hidden so that we can submit form by pressing enter */}
      <input type='submit' className='hidden' />
    </form>
  );
};
